// components/PyrenzMessageBox.tsx
import React, { useState, useEffect } from 'react';
import { Box, TextField, styled, SxProps, Typography } from '@mui/material';
import { CustomMarkdown, TypingIndicator, MessageNav } from '@client/components';
import { PyrenzBlueButton, ProfileAvatar } from '.';
import type { Message } from '@client/components';
import { getMessageCurrentIndex, saveMessageCurrentIndex } from '@client/utility';
import { useChatStore, useUserStore } from '@client/store';

interface PyrenzMessageBoxProps {
  role: 'user' | 'char';
  msg?: Message;
  userAvatar?: string;
  charAvatar?: string;
  isEditing?: boolean;
  localEditedMessage?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  isLoading?: boolean;
  sx?: SxProps;
  onClick?: (e: React.MouseEvent) => void;
  disableReplacement?: boolean;
  isGeneratingEmptyCharMessage?: boolean;
  content?: string;
}

const StyledBox = styled(Box)<{ role: 'user' | 'char' }>(({ role }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '10px 15px',
  borderRadius: '18px',
  margin: '10px',
  backgroundColor: role === 'user' ? '#555' : 'rgba(20,24,28,0.6)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#fff',
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
  position: 'relative',
  cursor: 'pointer',
  maxWidth: '100%',
  width: 'fit-content',
  transition: 'all 0.25s ease',
  '&:hover': {
    backgroundColor: role === 'user' ? '#444' : 'rgba(20,24,28,0.5)',
    boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
    transform: 'translateY(-2px)',
  },
}));

export function PyrenzMessageBox({
  role,
  msg,
  userAvatar,
  charAvatar,
  isEditing = false,
  localEditedMessage = '',
  onChange,
  onSaveEdit,
  onCancelEdit,
  isLoading = false,
  sx,
  onClick,
  disableReplacement = false,
  isGeneratingEmptyCharMessage = false,
  content,
}: PyrenzMessageBoxProps): JSX.Element {
  const { character } = useChatStore();
  const { username, personaName } = useUserStore();
  const [displayedText, setDisplayedText] = useState('');
  const [streamEnded, setStreamEnded] = useState(false);

  // Build alternatives array: [main_text, ...alternative_messages]
  const alternatives: string[] = React.useMemo(() => {
    const alts: string[] = [];
    if (msg?.text) alts.push(msg.text);
    if (msg?.alternative_messages?.length) {
      msg.alternative_messages.forEach(alt => {
        if (alt && !alts.includes(alt)) alts.push(alt);
      });
    }
    return alts;
  }, [msg?.text, msg?.alternative_messages]);

  const [altIndex, setAltIndex] = useState(0);

  // Load saved index from localForage on mount
  useEffect(() => {
    if (msg?.id && msg.id > 0) {  // Only for real messages, not temp (id: -1)
      getMessageCurrentIndex(msg.id).then((storedCurrent) => {
        if (storedCurrent !== null && storedCurrent > 0) {
          setAltIndex(Math.min(storedCurrent - 1, alternatives.length - 1));
        }
      });
    }
  }, [msg?.id]);

  // Update displayed text when message changes
  useEffect(() => {
    if (alternatives.length > 0) {
      setDisplayedText(alternatives[altIndex] || alternatives[0] || '');
    } else if (msg?.chunks?.length) {
      setDisplayedText(msg.chunks.join(''));
    } else if (content) {
      setDisplayedText(content);
    } else {
      setDisplayedText('');
    }

    // Check if stream ended (for generation)
    if (msg && !msg.isGenerate && !msg.chunks?.length && !msg.text && alternatives.length === 0) {
      setStreamEnded(true);
    } else {
      setStreamEnded(false);
    }
  }, [alternatives, altIndex, msg?.chunks, msg?.isGenerate, msg?.text, content]);

  const handleClick = (e: React.MouseEvent) => onClick?.(e);

  const totalMessages = alternatives.length;
  const currentMessage = alternatives[altIndex] ?? '';

  const showTypingIndicator =
    role === 'char' &&
    (isGeneratingEmptyCharMessage || (!msg?.chunks?.length && !msg?.text && alternatives.length === 0 && !streamEnded));

  const displayName =
    role === 'char'
      ? msg?.name || character?.name || 'Character'
      : msg?.name || personaName || username || 'User';

  const handlePrev = () => {
    setAltIndex((prev) => {
      const nextIndex = prev > 0 ? prev - 1 : prev;
      if (msg && msg.id > 0) {
        saveMessageCurrentIndex(msg.id, nextIndex + 1);
      }
      return nextIndex;
    });
  };

  const handleNext = () => {
    setAltIndex((prev) => {
      const nextIndex = prev < totalMessages - 1 ? prev + 1 : prev;
      if (msg && msg.id > 0) {
        saveMessageCurrentIndex(msg.id, nextIndex + 1);
      }
      return nextIndex;
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={role === 'user' ? 'flex-end' : 'flex-start'}
      sx={{ width: '100%' }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent={role === 'user' ? 'flex-end' : 'flex-start'}
        width="100%"
      >
        {role === 'char' && (
          <ProfileAvatar
            src={charAvatar || ''}
            alt={displayName}
            fallback={displayName?.[0] || 'C'}
            sx={{ width: 32, height: 32, mr: 1 }}
          />
        )}

        <Box display="flex" flexDirection="column" alignItems={role === 'user' ? 'flex-end' : 'flex-start'}>
          <StyledBox role={role} onClick={handleClick} sx={sx}>
            {isEditing ? (
              <>
                <TextField
                  value={localEditedMessage}
                  onChange={onChange}
                  autoFocus
                  multiline
                  fullWidth
                  minRows={3}
                  maxRows={20}
                  sx={{
                    '& .MuiOutlinedInput-root': { padding: '8px', '& fieldset': { border: 'none' } },
                    '& textarea': { overflow: 'auto', resize: 'vertical' },
                  }}
                />
                <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                  <PyrenzBlueButton onClick={onSaveEdit} disabled={isLoading} sx={{ minWidth: '80px' }}>
                    {isLoading ? 'Saving...' : 'Submit'}
                  </PyrenzBlueButton>
                  <PyrenzBlueButton onClick={onCancelEdit} disabled={isLoading} sx={{ minWidth: '80px' }}>
                    Cancel
                  </PyrenzBlueButton>
                </Box>
              </>
            ) : showTypingIndicator ? (
              <TypingIndicator />
            ) : streamEnded ? (
              <Typography color="warning.main" sx={{ fontStyle: 'italic' }}>
                [Stream Ended, Either Regenerate or Delete]
              </Typography>
            ) : (
              <CustomMarkdown 
                text={currentMessage || displayedText} 
                dataState={role} 
                disableReplacement={disableReplacement} 
                msgName={displayName} 
              />
            )}

            {role === 'char' && totalMessages > 1 && (
              <MessageNav
                altIndex={altIndex}
                totalMessages={totalMessages}
                onGoPrev={handlePrev}
                onGoNext={handleNext}
              />
            )}
          </StyledBox>
        </Box>

        {role === 'user' && (
          <ProfileAvatar 
            src={userAvatar || ''} 
            alt={displayName} 
            fallback={displayName?.[0] || 'U'} 
            sx={{ width: 32, height: 32, ml: 1 }} 
          />
        )}
      </Box>
    </Box>
  );
}